<script lang="ts">
  import type { GameData } from '../data.js';
  import type { SlotKey, GearSlot, AttrStat } from '../build-state.svelte.js';
  import { MOD_SLOT_KEYS } from '../build-state.svelte.js';
  import { GEAR_MODS, CATEGORY_LABELS } from '../data/gear-mods.js';
  import { i18next } from '../i18n.js';
  import { t, lang as langState } from '../lang-state.svelte.js';
  import GearPickerModal from './GearPickerModal.svelte';
  import RenderColorText from './RenderColorText.svelte';
  import { brandRoles, setRoles, ROLE_LABEL, type Role } from '../data/role-canon.js';
  import { loadPrototypeData, type PrototypeAugment } from '../data/prototype-augments.js';

  interface Props {
    slot: SlotKey;
    slotState: GearSlot;
    data: GameData;
    onBrandChange: (id: string | null) => void;
    onSetChange: (id: string | null) => void;
    onNamedChange: (id: string | null) => void;
    onCoreChange: (stat: GearSlot['coreStat']) => void;
    onAttrChange: (which: 'attr1' | 'attr2', stat: AttrStat | null) => void;
    onModChange: (id: string | null) => void;
    // Talent (chest/backpack only)
    talentId?: string | null;
    talentActive?: boolean;
    onTalentChange?: (id: string | null) => void;
    onTalentActiveChange?: (v: boolean) => void;
    talentOptions?: Array<{ id: string; name: string; note: string }>;
    talentLocked?: boolean;
    talentLockedDisplayId?: string | null;
    inputMode?: 'gear' | 'stats';
    exoticTalentActive?: boolean;
    onExoticTalentToggle?: (v: boolean) => void;
    onPrototypeChange?: (v: boolean) => void;
    onPrototypeAugmentChange?: (id: string | null) => void;
  }

  let { slot, slotState: s, data, onBrandChange, onSetChange, onNamedChange, onCoreChange, onAttrChange, onModChange,
        talentId, talentActive, onTalentChange, onTalentActiveChange, talentOptions, talentLocked, talentLockedDisplayId,
        inputMode = 'gear',
        exoticTalentActive = false, onExoticTalentToggle,
        onPrototypeChange, onPrototypeAugmentChange }: Props = $props();
  let statsMode = $derived(inputMode === 'stats');
  let lang = $derived(langState.current);

  let pickerOpen = $state(false);

  // Derived kind (for styling + conditional UI): which group is currently selected.
  type Group = 'brand' | 'set' | 'named' | 'exotic';
  let group: Group = $derived.by(() => {
    if (s.namedId) {
      const ng = data.namedGear.find((n) => n.id === s.namedId);
      return ng?.isExotic ? 'exotic' : 'named';
    }
    return s.setId ? 'set' : 'brand';
  });

  let brandOptions = $derived.by(() => {
    void lang;
    return [...data.brands]
      .map((b) => ({ id: b.id, name: i18next.t(b.id, { ns: 'brands', defaultValue: b.id }) as string }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  let setOptions = $derived.by(() => {
    void lang;
    return [...data.sets]
      .map((s) => ({ id: s.id, name: i18next.t(s.id, { ns: 'gear-sets', defaultValue: s.id }) as string }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  let namedOptions = $derived.by(() => {
    void lang;
    return data.namedGear
      .filter((n) => n.slot === slot)
      .map((n) => ({ id: n.id, name: i18next.t(n.id, { ns: 'named-gear', defaultValue: n.id }) as string }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  // Gear attribute pool (from game data gear_attribute_pool.json, TU22.1).
  // All 13 attrs available per slot — player picks any 2 for brand/set gear.
  // mag/reload are NOT gear attrs (they're weapon mod stats), so excluded here.
  const ATTR_KEYS: AttrStat[] = ['chc', 'chd', 'hsd', 'wd', 'dta', 'dth', 'ooc', 'handling'];

  let attrOptions = $derived.by(() => {
    void lang;
    return ATTR_KEYS.map((k) => ({
      key: k,
      name: i18next.t(k, { ns: 'stats', defaultValue: k }) as string,
    }));
  });

  let namedBonus = $derived.by(() => {
    void lang;
    if (!s.namedId) return '';
    if (!i18next.exists(s.namedId, { ns: 'named-bonus' })) return '';
    return i18next.t(s.namedId, { ns: 'named-bonus', defaultValue: '' }) as string;
  });

  const SLOT_ICONS: Record<SlotKey, string> = {
    chest: '🦺', backpack: '🎒', gloves: '🧤', mask: '😷', holster: '🔫', kneepads: '🦵',
  };

  let hasTalent = $derived(slot === 'chest' || slot === 'backpack');
  let hasModSlot = $derived(MOD_SLOT_KEYS.has(slot));

  // Prototype Augments — lazy-loaded from /data/prototype-augments.json.
  // Only meaningful on brand/set gear (named/exotic items are themselves "unique" and
  // don't take the prototype tag in-game).
  let prototypeAugments = $state<PrototypeAugment[]>([]);
  $effect(() => {
    loadPrototypeData().then((d) => {
      prototypeAugments = d.augments;
    }).catch(() => { /* ignore — feature degrades silently */ });
  });
  let canBePrototype = $derived(group === 'brand' || group === 'set');
  let currentAugment = $derived(
    s.prototypeAugmentId ? prototypeAugments.find((a) => a.id === s.prototypeAugmentId) ?? null : null
  );
  let augmentTooltip = $derived(
    currentAugment ? (lang === 'ru' ? currentAugment.description_ru : currentAugment.description_en) : ''
  );

  let coreColorClass = $derived('core-' + s.coreStat);

  let currentLabel = $derived(
    (group === 'named' || group === 'exotic') && s.namedId
      ? (namedOptions.find((n) => n.id === s.namedId)?.name ?? s.namedId)
      : group === 'set' && s.setId
      ? (setOptions.find((opt) => opt.id === s.setId)?.name ?? s.setId)
      : group === 'brand' && s.brandId
      ? (brandOptions.find((b) => b.id === s.brandId)?.name ?? s.brandId)
      : ''
  );
  let currentBadge = $derived.by(() => {
    if (s.namedId) {
      const ng = data.namedGear.find((n) => n.id === s.namedId);
      return ng?.isExotic ? 'EXOTIC' : 'NAMED';
    }
    return s.setId ? 'SET' : s.brandId ? 'BRAND' : '';
  });
  let currentRoles = $derived.by<Role[]>(() => {
    if (s.brandId && !s.setId && !s.namedId) return brandRoles(s.brandId);
    if (s.setId && !s.namedId) {
      const gs = data.byId.set.get(s.setId);
      return setRoles(s.setId, gs?.type);
    }
    return [];
  });
</script>

<div class="gslot">
  <div class="gslot-title">
    <span class="ico">{SLOT_ICONS[slot]}</span>
    <span class="slot-label">{t('ui', slot)}</span>
    {#if currentBadge}<span class="kind-badge kind-{group}">{currentBadge}</span>{/if}
    {#each currentRoles as r (r)}<span class="role-tag {r}">{ROLE_LABEL[r]}</span>{/each}
  </div>

  <div class="gslot-row row-1">
    <button type="button" class="input sel group-sel picker-btn kind-{group}" onclick={() => (pickerOpen = true)}>
      {currentLabel || (lang === 'ru' ? '— Выбрать —' : '— Pick —')} <span class="caret">▼</span>
    </button>
    {#if !statsMode}
      <select
        class="input {coreColorClass}"
        value={s.coreStat}
        onchange={(e) => onCoreChange((e.currentTarget as HTMLSelectElement).value as GearSlot['coreStat'])}
      >
        <option value="wd">WD</option>
        <option value="armor">ARM</option>
        <option value="skill_tier">SKL</option>
      </select>
    {/if}
  </div>

  {#if (group === 'named' || group === 'exotic') && s.namedId}
    {@const ng = data.namedGear.find((n) => n.id === s.namedId)}
    {#if namedBonus}<div class="named-bonus">🔒 <RenderColorText text={namedBonus} /></div>{/if}
    {#if ng?.isExotic && ng.activeBonuses && ng.activeBonuses.length > 0 && onExoticTalentToggle}
      <label class="exotic-toggle">
        <input type="checkbox" checked={exoticTalentActive} onchange={(e) => onExoticTalentToggle((e.currentTarget as HTMLInputElement).checked)} />
        <span>⚡ {lang === 'ru' ? 'Талант активен' : 'Talent active'}</span>
        <span class="exo-bonus">
          {#each ng.activeBonuses as b, i (i)}{i > 0 ? ' · ' : ''}+{b.value}% {i18next.t(b.stat, { ns: 'stats', defaultValue: b.stat })}{#if b.amp} (amp){/if}{/each}
        </span>
      </label>
    {/if}
    <a class="named-info" href={`#item=${encodeURIComponent(s.namedId)}`}>📄 {lang === 'ru' ? 'Детали · где взять' : 'Details · where to get'} ↗</a>
  {/if}

  {#if !statsMode}
    {#if (group === 'named' || group === 'exotic') && s.namedId}
      {@const ng = data.namedGear.find((n) => n.id === s.namedId)}
      {@const fixed = ng?.fixedAttrs ?? []}
      <div class="gslot-row attr-row named-locked-row">
        {#if fixed.length > 0}
          <div class="locked-attr">
            🔒 +{fixed[0].value}% {i18next.t(fixed[0].stat, { ns: 'stats', defaultValue: fixed[0].stat })}
          </div>
        {:else}
          <select
            class="input sel attr"
            value={s.attr1 ?? ''}
            onchange={(e) => onAttrChange('attr1', ((e.currentTarget as HTMLSelectElement).value || null) as AttrStat | null)}
          >
            <option value="">— attr1 —</option>
            {#each attrOptions as a (a.key)}
              <option value={a.key}>{a.name}</option>
            {/each}
          </select>
        {/if}
        {#if fixed.length > 1}
          <div class="locked-attr">
            🔒 +{fixed[1].value}% {i18next.t(fixed[1].stat, { ns: 'stats', defaultValue: fixed[1].stat })}
          </div>
        {:else}
          <select
            class="input sel attr"
            value={s.attr2 ?? ''}
            onchange={(e) => onAttrChange('attr2', ((e.currentTarget as HTMLSelectElement).value || null) as AttrStat | null)}
          >
            <option value="">— attr2 —</option>
            {#each attrOptions as a (a.key)}
              <option value={a.key}>{a.name}</option>
            {/each}
          </select>
        {/if}
      </div>
    {:else if group === 'set'}
      <!-- Set gear: only 1 attribute slot (2nd slot reserved for set bonus) -->
      <div class="gslot-row attr-row set-attrs">
        <select
          class="input sel attr"
          value={s.attr1 ?? ''}
          onchange={(e) => onAttrChange('attr1', ((e.currentTarget as HTMLSelectElement).value || null) as AttrStat | null)}
        >
          <option value="">— attr —</option>
          {#each attrOptions as a (a.key)}
            <option value={a.key}>{a.name}</option>
          {/each}
        </select>
        <div class="set-slot-info">🟢 {lang === 'ru' ? 'Бонус сета' : 'Set bonus'}</div>
      </div>
    {:else}
      <!-- Brand gear: 2 attribute slots -->
      <div class="gslot-row attr-row">
        <select
          class="input sel attr"
          value={s.attr1 ?? ''}
          onchange={(e) => onAttrChange('attr1', ((e.currentTarget as HTMLSelectElement).value || null) as AttrStat | null)}
        >
          <option value="">— attr1 —</option>
          {#each attrOptions as a (a.key)}
            <option value={a.key}>{a.name}</option>
          {/each}
        </select>
        <select
          class="input sel attr"
          value={s.attr2 ?? ''}
          onchange={(e) => onAttrChange('attr2', ((e.currentTarget as HTMLSelectElement).value || null) as AttrStat | null)}
        >
          <option value="">— attr2 —</option>
          {#each attrOptions as a (a.key)}
            <option value={a.key}>{a.name}</option>
          {/each}
        </select>
      </div>
    {/if}
  {/if}

  {#if hasTalent && talentOptions && onTalentChange && group !== 'named' && group !== 'exotic'}
    <div class="gslot-row tal-row">
      <span class="tal-label">🎯 TALENT</span>
      {#if talentLocked}
        {@const lockedId = talentLockedDisplayId ?? talentId}
        {@const lockedName = lockedId ? (talentOptions?.find((t) => t.id === lockedId)?.name ?? lockedId) : '—'}
        <div class="tal-locked" title={lang === 'ru' ? 'Талант сета — учитывается автоматически' : 'Set talent — applied automatically'}>
          <span class="lock">🔒</span>
          <span class="tal-name">{lockedName}</span>
        </div>
      {:else}
        <select
          class="input sel"
          value={talentId ?? ''}
          onchange={(e) => onTalentChange((e.currentTarget as HTMLSelectElement).value || null)}
        >
          <option value="">— —</option>
          {#each talentOptions as tl (tl.id)}
            <option value={tl.id}>{tl.name}</option>
          {/each}
        </select>
      {/if}
      {#if talentId && !talentLocked && onTalentActiveChange}
        <label class="tal-toggle">
          <input type="checkbox" checked={talentActive} onchange={(e) => onTalentActiveChange((e.currentTarget as HTMLInputElement).checked)} />
          <span>on</span>
        </label>
      {:else}
        <span></span>
      {/if}
    </div>
  {/if}

  {#if canBePrototype && !statsMode && onPrototypeChange}
    <div class="gslot-row proto-row">
      <label class="proto-toggle">
        <input
          type="checkbox"
          checked={s.isPrototype}
          onchange={(e) => onPrototypeChange((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>🧪 {lang === 'ru' ? 'Прототип' : 'Prototype'}</span>
      </label>
      {#if s.isPrototype && onPrototypeAugmentChange}
        <select
          class="input sel"
          value={s.prototypeAugmentId ?? ''}
          onchange={(e) => onPrototypeAugmentChange((e.currentTarget as HTMLSelectElement).value || null)}
          title={augmentTooltip}
        >
          <option value="">— {lang === 'ru' ? 'Выбрать аугмент' : 'Choose Augment'} —</option>
          {#each prototypeAugments as aug (aug.id)}
            <option value={aug.id}>{lang === 'ru' ? aug.name_ru : aug.name_en}</option>
          {/each}
        </select>
      {/if}
    </div>
    {#if s.isPrototype && currentAugment}
      <div class="proto-tip">
        <strong>{lang === 'ru' ? currentAugment.name_ru : currentAugment.name_en}</strong>
        <span>· {augmentTooltip}</span>
      </div>
    {/if}
  {/if}

  {#if hasModSlot && !statsMode}
    <div class="gslot-row mod-row">
      <span class="mod-label">Mod</span>
      <select
        class="input sel attr"
        value={s.modAttr ?? ''}
        onchange={(e) => onModChange((e.currentTarget as HTMLSelectElement).value || null)}
      >
        <option value="">— {langState.current === 'ru' ? 'вставка' : 'gear mod'} —</option>
        <optgroup label="{langState.current === 'ru' ? CATEGORY_LABELS.offense.ru : CATEGORY_LABELS.offense.en}">
          {#each GEAR_MODS.filter((m) => m.category === 'offense') as m (m.id)}
            <option value={m.id}>{langState.current === 'ru' ? m.name.ru : m.name.en} +{m.value}%</option>
          {/each}
        </optgroup>
        <optgroup label="{langState.current === 'ru' ? CATEGORY_LABELS.defense.ru : CATEGORY_LABELS.defense.en}">
          {#each GEAR_MODS.filter((m) => m.category === 'defense') as m (m.id)}
            <option value={m.id}>{langState.current === 'ru' ? m.name.ru : m.name.en}</option>
          {/each}
        </optgroup>
        <optgroup label="{langState.current === 'ru' ? CATEGORY_LABELS.skill.ru : CATEGORY_LABELS.skill.en}">
          {#each GEAR_MODS.filter((m) => m.category === 'skill') as m (m.id)}
            <option value={m.id}>{langState.current === 'ru' ? m.name.ru : m.name.en} +{m.value}%</option>
          {/each}
        </optgroup>
      </select>
    </div>
  {/if}
</div>

{#if pickerOpen}
  <GearPickerModal
    {data}
    availableNamed={data.namedGear.filter((n) => n.slot === slot)}
    currentBrandId={s.brandId}
    currentSetId={s.setId}
    currentNamedId={s.namedId}
    onPickBrand={onBrandChange}
    onPickSet={onSetChange}
    onPickNamed={onNamedChange}
    onClose={() => (pickerOpen = false)}
  />
{/if}

<style>
  .picker-btn { cursor: pointer; text-align: left; display: flex; align-items: center; justify-content: space-between; }
  .picker-btn:hover { border-color: var(--border-hi); }
  .picker-btn .caret { color: var(--muted); font-size: 9px; margin-left: 6px; }
  .gslot { background: var(--bg-2); border: 1px solid var(--border); border-radius: var(--r); padding: 8px 10px; }
  .gslot-title {
    display: flex; align-items: center; gap: 6px;
    font: 700 10px/1 var(--f-display); letter-spacing: .14em;
    text-transform: uppercase; color: var(--orange);
    margin-bottom: 6px;
  }
  .gslot-title .ico { font-size: 12px; }
  .slot-label { flex: 1; }
  .gslot-row { display: grid; grid-template-columns: 1fr 1fr 60px; gap: 4px; }
  .row-1 { grid-template-columns: 1fr 60px; }
  .attr-row { grid-template-columns: 1fr 1fr; margin-top: 4px; }
  .tal-row {
    grid-template-columns: 80px 1fr 40px;
    margin-top: 4px; padding-top: 4px;
    border-top: 1px dashed var(--border);
    align-items: center;
  }
  .mod-row {
    grid-template-columns: 40px 1fr;
    margin-top: 4px; padding-top: 4px;
    border-top: 1px dashed var(--border);
    align-items: center;
  }
  .mod-label { font: 700 9px/1 var(--f-display); letter-spacing: .14em; text-transform: uppercase; color: var(--blue); text-align: center; }
  .sel { padding: 5px 6px; font-size: 11px; }
  .attr { font-size: 10px; color: var(--text-dim); }
  .tal-label { font: 700 10px/1 var(--f-display); letter-spacing: .1em; text-transform: uppercase; color: var(--orange); }
  .tal-locked { display: flex; align-items: center; gap: 6px; padding: 5px 8px; background: var(--bg-2); border: 1px solid var(--border); border-radius: var(--r-sm); color: var(--named); font-size: 11px; }
  .lock { font-size: 10px; }
  .tal-name { font: 700 10px/1 var(--f-display); letter-spacing: .04em; }
  .tal-toggle { display: flex; align-items: center; gap: 3px; font: 700 9px/1 var(--f-display); letter-spacing: .1em; text-transform: uppercase; color: var(--text-dim); cursor: pointer; justify-content: center; }
  .tal-toggle input { accent-color: var(--orange); }
  .named-bonus { font-size: 10px; color: var(--named); background: rgba(245,166,35,.08); padding: 4px 8px; margin-top: 4px; border-radius: 3px; border-left: 2px solid var(--named); font-style: italic; }
  .exotic-toggle { display: flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 10px; color: var(--exotic); cursor: pointer; padding: 4px 8px; background: rgba(255,107,61,.08); border: 1px solid rgba(255,107,61,.3); border-radius: 3px; flex-wrap: wrap; }
  .exotic-toggle input { accent-color: var(--exotic); }
  .exotic-toggle .exo-bonus { color: var(--set); font-family: var(--f-mono); font-size: 9px; }
  .named-info { display: inline-block; margin-top: 4px; font-size: 10px; color: var(--brand); text-decoration: none; padding: 3px 8px; background: rgba(216,221,232,.08); border: 1px solid rgba(216,221,232,.3); border-radius: 3px; }
  .named-locked-row { grid-template-columns: 1fr 1fr; }
  .locked-attr {
    font-size: 10px; color: var(--named);
    padding: 5px 8px;
    background: rgba(245,166,35,.08);
    border: 1px dashed rgba(245,166,35,.3);
    border-radius: 3px;
    display: flex; align-items: center; gap: 4px;
    font-style: italic;
  }
  .core-locked {
    display: flex; align-items: center; justify-content: center;
    padding: 5px 4px; font-size: 12px; color: var(--named);
    background: rgba(245,166,35,.08);
    border: 1px dashed rgba(245,166,35,.3); border-radius: 3px;
  }
  .kind-badge {
    font: 700 9px/1 var(--f-display); letter-spacing: .12em;
    padding: 3px 6px; border-radius: 3px;
    margin-left: auto;
  }
  .kind-badge.kind-brand  { color: #1a1c20; background: var(--brand);  border: 1px solid var(--brand-hi); }
  .kind-badge.kind-set    { color: #fff;    background: var(--set);    border: 1px solid var(--set-hi); }
  .kind-badge.kind-named  { color: #1a0f00; background: var(--named);  border: 1px solid var(--named-hi);
                            box-shadow: 0 0 12px -2px rgba(245,166,35,.5); }
  .kind-badge.kind-exotic { color: #2a0700; background: var(--exotic); border: 1px solid var(--exotic-hi);
                            box-shadow: 0 0 14px -2px rgba(255,107,61,.55); }
  .picker-btn.kind-brand  { border-left: 3px solid var(--brand); }
  .picker-btn.kind-set    { border-left: 3px solid var(--set); }
  .picker-btn.kind-named  { border-left: 3px solid var(--named); }
  .picker-btn.kind-exotic { border-left: 3px solid var(--exotic); }
  .set-attrs { grid-template-columns: 1fr 1fr; }
  .set-slot-info {
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; color: var(--set); font-style: italic;
    padding: 5px 8px;
    background: rgba(61,166,120,.08);
    border: 1px dashed rgba(61,166,120,.3); border-radius: 3px;
  }
  .named-info:hover { background: rgba(216,221,232,.18); }
  .proto-row {
    grid-template-columns: auto 1fr;
    gap: 8px;
    margin-top: 4px; padding-top: 4px;
    border-top: 1px dashed var(--border);
    align-items: center;
  }
  .proto-toggle {
    display: flex; align-items: center; gap: 4px;
    font: 700 10px/1 var(--f-display); letter-spacing: .08em;
    text-transform: uppercase; color: var(--blue, #4ea7ff);
    cursor: pointer; white-space: nowrap;
  }
  .proto-toggle input { accent-color: var(--blue, #4ea7ff); }
  .proto-tip {
    margin-top: 3px; padding: 4px 8px;
    font-size: 10px; line-height: 1.35;
    color: var(--text-dim);
    background: rgba(78,167,255,.08);
    border-left: 2px solid var(--blue, #4ea7ff);
    border-radius: 3px;
  }
  .proto-tip strong { color: var(--blue, #4ea7ff); margin-right: 4px; }
</style>
